import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  TextField,
} from "@mui/material";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useAppContext } from "../../context/AppContext";
import { primary } from "../../constants/colors";

import { GetAppointmentDetails } from "../../helpers/Appointment";
import {
  deleteVideoSessionCapture,
  getVideoSessionCaptureBlob,
  listVideoSessionCaptures,
  saveVideoSessionNote,
} from "../../helpers/VideoSessions";
import {
  downloadMedicalFormByChatId,
  downloadReportByChatId,
} from "../../helpers/Downloads";
import Toast from "../../components/toast";
import moment from "moment";
import { minutesToTime } from "../../constants/timeUtils";
import { openPhysicianSession } from "../../helpers/VideoSessions/openPhysicianSession";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";

export default function PatientDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useAppContext();
  const { appointmentId } = location.state || {};

  const [isLoading, setIsLoading] = useState(false);
  const [appointmentData, setAppointmentData] = useState([]);

  const GetAllAppointmenthanlder = async (_appointmentId) => {
    setIsLoading(true);
    const response = await GetAppointmentDetails(_appointmentId);
    if (response.statusCode === 200 && response.isSuccess === true) {
      const data = response.data;
      setAppointmentData(data);
    } else {
      Toast.error(response.message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (appointmentId) {
      GetAllAppointmenthanlder(appointmentId);
    }
  }, [appointmentId]);

  const handleEditClick = () => {
    navigate("/physician/review-patient-form", {
      state: { chatId: appointmentData.chatId },
    });
  };

  return (
    <Box>

      <Typography
        variant="body2"
        color="primary"
        mb={2}
        sx={{
          textDecoration: "underline",
          cursor: "pointer",
        }}
        onClick={() => navigate(-1)}
      >
        <KeyboardBackspaceOutlinedIcon
          fontSize="small"
          sx={{ verticalAlign: "middle", mr: 0.5, fontWeight: "medium" }}
        />
        Back
      </Typography>

      <Typography variant="h5" sx={{ mb: 3 }}>
        Patient Name:{" "}
        <Box component="span" sx={{ color: "#6C74E6", fontWeight: 600 }}>
          {appointmentData.patientName}
        </Box>
      </Typography>

      <Grid container spacing={2}>
        {/* LEFT SECTION */}
        <Grid item size={{ xs: 12, sm: 6, md: 8 }}>
          <MedicalFormsCard
            appointmentData={appointmentData}
            handleEditClick={handleEditClick}
          />
          <PreviousTestsCard appointmentData={appointmentData} />
          <SessionNotesCard
            appointmentData={appointmentData}
            onSaved={() => GetAllAppointmenthanlder(appointmentId)}
          />
          <SessionCapturesCard appointmentId={appointmentData._id} />
        </Grid>

        {/* RIGHT SECTION */}
        <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
          <AppointmentDetailsCard appointmentData={appointmentData} />
        </Grid>
      </Grid>
    </Box>
  );
}

function SessionCapturesCard({ appointmentId }) {
  const [captures, setCaptures] = useState([]);
  const [contentUrls, setContentUrls] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!appointmentId) return undefined;
    let cancelled = false;
    const urls = [];
    const load = async () => {
      setLoading(true);
      const response = await listVideoSessionCaptures(appointmentId);
      if (!response?.isSuccess || cancelled) {
        setLoading(false);
        return;
      }
      const ready = (response.data || []).filter(
        (capture) => capture.status === "ready",
      );
      setCaptures(response.data || []);
      const entries = await Promise.all(
        ready.map(async (capture) => {
          try {
            const blob = await getVideoSessionCaptureBlob(
              appointmentId,
              capture.id,
            );
            const url = URL.createObjectURL(blob);
            urls.push(url);
            return [capture.id, url];
          } catch (error) {
            return [capture.id, ""];
          }
        }),
      );
      if (!cancelled) setContentUrls(Object.fromEntries(entries));
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [appointmentId]);

  const handleDelete = async (captureId) => {
    if (!window.confirm("Delete this session capture permanently?")) return;
    const response = await deleteVideoSessionCapture(
      appointmentId,
      captureId,
    );
    if (!response?.isSuccess) {
      Toast.error(response?.message || "Unable to delete capture.");
      return;
    }
    if (contentUrls[captureId]) URL.revokeObjectURL(contentUrls[captureId]);
    setCaptures((current) =>
      current.filter((capture) => capture.id !== captureId),
    );
    setContentUrls((current) => {
      const next = { ...current };
      delete next[captureId];
      return next;
    });
    Toast.success("Capture deleted.");
  };

  const handleDownload = (capture) => {
    const url = contentUrls[capture.id];
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `${capture.type}-${capture.id}.${
      capture.type === "audio" ? "wav" : "jpg"
    }`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Card
      sx={{
        borderRadius: 1.5,
        border: "1px solid #E6E8EC",
        boxShadow: "none",
        marginBottom: 2,
      }}
    >
      <CardContent>
        <Typography fontWeight={600} mb={1}>
          Session Captures
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading captures...
          </Typography>
        ) : captures.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No screenshots or audio recordings saved.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {captures.map((capture) => (
              <Grid item size={{ xs: 12, md: 6 }} key={capture.id}>
                <Box
                  sx={{
                    border: "1px solid #E6E8EC",
                    p: 1.25,
                    minHeight: 120,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {capture.type === "audio"
                      ? "Audio recording"
                      : "Screenshot"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {capture.targetDeviceLabel || "Equipment"} {" | "}
                    {capture.status}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {capture.createdAt
                      ? moment(capture.createdAt).format(
                          "MMM D, YYYY h:mm A",
                        )
                      : ""}
                    {capture.type === "audio" && capture.durationSeconds
                      ? ` | ${Math.round(capture.durationSeconds)} seconds`
                      : ""}
                  </Typography>
                  {capture.type === "screenshot" &&
                    contentUrls[capture.id] && (
                      <Box
                        component="img"
                        src={contentUrls[capture.id]}
                        alt="Session screenshot"
                        sx={{
                          display: "block",
                          width: "100%",
                          maxHeight: 220,
                          objectFit: "contain",
                          bgcolor: "#111",
                          mt: 1,
                        }}
                      />
                    )}
                  {capture.type === "audio" &&
                    contentUrls[capture.id] && (
                      <Box
                        component="audio"
                        controls
                        src={contentUrls[capture.id]}
                        sx={{ width: "100%", mt: 1 }}
                      />
                    )}
                  <Button
                    size="small"
                    sx={{ mt: 1, mr: 1 }}
                    disabled={!contentUrls[capture.id]}
                    onClick={() => handleDownload(capture)}
                  >
                    Download
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    sx={{ mt: 1 }}
                    onClick={() => handleDelete(capture.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

function SessionNotesCard({ appointmentData, onSaved }) {
  const [noteText, setNoteText] = useState(
    appointmentData.videoSessionNote?.noteText || "",
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNoteText(appointmentData.videoSessionNote?.noteText || "");
  }, [appointmentData.videoSessionNote?.noteText]);

  const handleSave = async () => {
    if (!appointmentData?._id) return;
    setSaving(true);
    const response = await saveVideoSessionNote(appointmentData._id, {
      noteText,
    });
    setSaving(false);
    if (response?.isSuccess) {
      Toast.success("Notes saved.");
      onSaved?.();
    } else {
      Toast.error(response?.message || "Unable to save notes.");
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 1.5,
        border: "1px solid #E6E8EC",
        boxShadow: "none",
        marginBottom: 2,
      }}
    >
      <CardContent>
        <Typography fontWeight={600} mb={1}>
          Private Meeting Notes
        </Typography>
        <TextField
          value={noteText}
          onChange={(event) => setNoteText(event.target.value)}
          multiline
          minRows={4}
          fullWidth
          placeholder="No notes saved yet."
          size="small"
        />
        <Button
          sx={{ mt: 1, textTransform: "none" }}
          variant="outlined"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Notes"}
        </Button>
      </CardContent>
    </Card>
  );
}
function MedicalFormsCard({ appointmentData, handleEditClick }) {
  const handleViewMedicalForm = async (formType) => {
    downloadMedicalFormByChatId(appointmentData.chatId, formType);
  };

  return (
    <Card
      sx={{
        borderRadius: 1.5,
        border: "1px solid #E6E8EC",
        boxShadow: "none",
        marginBottom: 2,
      }}
    >
      <CardContent>
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography fontWeight={600}>Medical Forms</Typography>

            <Typography variant="body2" color="text.secondary">
              Review the patient's submitted PME and POLST documentation.
            </Typography>
          </Box>

          <Button
            startIcon={<EditIcon />}
            sx={{
              textTransform: "none",
              border: "1px solid #E3E3E5",
              color: primary,
              whiteSpace: "nowrap",
            }}
            onClick={handleEditClick}
          >
            Review & Edit Form
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
            <Card
              sx={{
                borderRadius: 1.5,
                border: "1px solid #E6E8EC",
                boxShadow: "none",
                backgroundColor: "#FCFCFC",
              }}
            >
              <CardContent>
                <Typography
                  sx={{
                    color: primary,
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  "PME Medical Form"
                </Typography>

                <Typography variant="body2" color="text.secondary" mb={2}>
                  Document • Generated on:{" "}
                  {`${appointmentData.createdAt ? moment(appointmentData.createdAt).format("Do MMM YYYY") : "N/A"}`}
                </Typography>

                <Button
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    border: "1px solid #E3E3E5",
                    color: primary,
                  }}
                  onClick={() => handleViewMedicalForm("PME")}
                >
                  Download
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {appointmentData?.evaluationType === "PME & POLST" && (
            <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
              <Card
                sx={{
                  borderRadius: 1.5,
                  border: "1px solid #E6E8EC",
                  boxShadow: "none",
                  backgroundColor: "#FCFCFC",
                }}
              >
                <CardContent>
                  <Typography
                    sx={{
                      color: primary,
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    "POLST Medical Form"
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Document • Generated on:{" "}
                    {`${appointmentData.createdAt ? moment(appointmentData.createdAt).format("Do MMM YYYY") : "N/A"}`}
                  </Typography>

                  <Button
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      border: "1px solid #E3E3E5",
                      color: primary,
                    }}
                    onClick={() => handleViewMedicalForm("POLST")}
                  >
                    Download
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* <Grid size={{ xs: 12 }}>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "end", p: 1, borderRadius: 1, backgroundColor: "#F5F5FF", border: "1px solid #7B7DE5" }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <EditIcon color="primary" fontSize="22px" />
                  <Typography fontSize={18} color="primary">
                    Last Edited
                  </Typography>
                </Box>
                <Typography fontSize={14} color="text.primary">
                  john.doe@email.com
                </Typography>
              </Box>
              <Box>
                <Typography fontSize={12} color="primary">
                  12 June, 2026
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  04:25 PM EST
                </Typography>
              </Box>
            </Box>

          </Grid> */}
        </Grid>
      </CardContent>
    </Card>
  );
}

function AppointmentDetailsCard({ appointmentData }) {
  const handleJoinMeeting = async (row) => {
    if (!row?._id) {
      Toast.error("Appointment is not available");
      return;
    }

    const isProviderVisit =
      row.isProviderVisit || row.appointmentType === "provider_visit";

    if (isProviderVisit) {
      openPhysicianSession(row._id);
      return;
    }

    if (!row.zoomStartUrl) {
      Toast.error("Zoom link is not available");
      return;
    }

    window.open(row.zoomStartUrl, "_blank");
  };
  return (
    <Card sx={{ width: "100%", borderRadius: 1.5, boxShadow: "none" }}>
      <CardContent>
        <Typography fontWeight={600} mb={2}>
          Appointment Details
        </Typography>

        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                backgroundColor: "#F5F5FF",
                borderRadius: "8px",
                width: 30,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CalendarMonthIcon sx={{ color: primary, fontSize: "22px" }} />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Date
              </Typography>
              <Typography sx={{ color: "#1E1E1EBF" }}>
                {appointmentData.appointmentDate
                  ? moment(appointmentData.appointmentDate).format(
                    "MMMM Do, YYYY",
                  )
                  : "N/A"}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                backgroundColor: "#F5F5FF",
                borderRadius: "8px",
                width: 30,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccessTimeIcon sx={{ color: primary, fontSize: "22px" }} />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Time
              </Typography>
              <Typography sx={{ color: "#1E1E1EBF" }}>
                {`${minutesToTime(appointmentData.startMinute)} - ${minutesToTime(appointmentData.endMinute)}`}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                backgroundColor: "#F5F5FF",
                borderRadius: "8px",
                width: 30,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocationOnIcon sx={{ color: primary, fontSize: "22px" }} />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Location
              </Typography>

              <Typography sx={{ color: "#1E1E1EBF" }}>
                Embedded provider visit session
              </Typography>

              <Typography
                sx={{
                  color: primary,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => handleJoinMeeting(appointmentData)}
              >
                Open Session
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function PreviousTestsCard({ appointmentData }) {
  const handleViewMedicalForm = async () => {
    downloadReportByChatId(appointmentData.chatId);
  };
  return (
    <Card
      sx={{
        borderRadius: 1.5,
        border: "1px solid #E6E8EC",
        boxShadow: "none",
        backgroundColor: "#ffffff",
      }}
    >
      <CardContent>
        <Typography fontWeight={600}>Previous Medical Tests</Typography>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Uploaded by patient for review
        </Typography>

        {appointmentData.reportUploadStatus === true ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <PictureAsPdfIcon sx={{ color: "#E53935" }} />

              <Box>
                <Typography sx={{ color: primary }}>X-Ray Report</Typography>

                <Typography variant="body2" color="text.secondary">
                  Document • Uploaded:{" "}
                  {`${appointmentData.reportUploadDate ? moment(appointmentData.reportUploadDate).format("Do MMM YYYY") : "N/A"}`}
                </Typography>
              </Box>
            </Stack>

            <Button
              sx={{
                textTransform: "none",
                border: "1px solid #E3E3E5",
                borderRadius: 2,
                color: primary,
              }}
              onClick={() => handleViewMedicalForm("POLST")}
            >
              Download
            </Button>
          </Stack>
        ) : (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <PictureAsPdfIcon sx={{ color: "#E53935" }} />

              <Box>
                <Typography sx={{ color: primary }}>X-Ray Report</Typography>

                <Typography variant="body2" color="text.secondary">
                  No reports uploaded yet.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
